import { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { putFetcher, getFetcher } from "api/swr";
import { toast } from "@/components/ui/use-toast";
import { SpineTemplate } from "../../../types/spine-template";
import type { Template } from "types/template";
import { TemplateList } from "./TemplateList";
import { SelectedTemplatesList } from "./SelectedTemplatesList";
import { normalizeRelatedTemplateIds } from "./relatedTemplateOrder";

export type RelatedTemplatesPanelProps = {
  /**
   * 头像模版 id：从 `/templates/:id` 读写 `relatedSpineTemplateIds`，
   * 保存时为 `PUT /templates/:id` + `{ key, value }`。
   * 不传则沿用路由 `tid`，按 Spine 模版读写 `relatedTemplateIds`。
   */
  avatarTemplateId?: string;
};

export function RelatedTemplatesPanel({
  avatarTemplateId,
}: RelatedTemplatesPanelProps = {}) {
  const { tid: routeTid } = useParams<{ tid: string }>();
  const isAvatarMode = Boolean(avatarTemplateId);
  const spineTid = isAvatarMode ? undefined : routeTid;
  const entityId = avatarTemplateId ?? spineTid ?? "";

  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [templateCache, setTemplateCache] = useState<
    Map<string, Partial<SpineTemplate>>
  >(new Map());
  const loadingRef = useRef<Set<string>>(new Set());
  const serverRelatedSigRef = useRef<string>("");

  const { data: spineTemplate, mutate: mutateSpineTemplate } =
    useSWR<SpineTemplate>(
      !isAvatarMode && spineTid ? [`/spine-templates/${spineTid}`] : null,
    );

  const { data: avatarTemplate, mutate: mutateAvatarTemplate } = useSWR<Template>(
    isAvatarMode && avatarTemplateId ? [`/templates/${avatarTemplateId}`] : null,
  );

  const mutateCurrentTemplate = isAvatarMode
    ? mutateAvatarTemplate
    : mutateSpineTemplate;

  const rawServerRelatedIds = isAvatarMode
    ? avatarTemplate?.relatedSpineTemplateIds
    : spineTemplate?.relatedTemplateIds;

  const saveEndpoint = isAvatarMode
    ? avatarTemplateId
      ? `/templates/${avatarTemplateId}`
      : null
    : spineTid
      ? `/spine-templates/${spineTid}`
      : null;

  const { trigger: saveRelatedTemplates, isMutating } = useSWRMutation(
    saveEndpoint,
    putFetcher,
    {
      onSuccess: () => {
        toast({
          title: "保存成功",
          description: "关联模板已更新",
          className: "bg-green-500 text-white",
        });
        mutateCurrentTemplate();
      },
      onError: (error: Error & { message?: string }) => {
        toast({
          variant: "destructive",
          title: "保存失败",
          description: error.message || "未知错误",
        });
      },
    },
  );

  const serverRelatedIds = rawServerRelatedIds ?? [];

  const hasChanges = useMemo(() => {
    if (serverRelatedIds.length !== selectedTemplateIds.length) return true;
    for (let i = 0; i < serverRelatedIds.length; i++) {
      if (serverRelatedIds[i] !== selectedTemplateIds[i]) return true;
    }
    return false;
  }, [serverRelatedIds, selectedTemplateIds]);

  const serverRelatedSig = useMemo(() => {
    const tidPart = entityId;
    return `${tidPart}\n${serverRelatedIds.join("\0")}`;
  }, [entityId, serverRelatedIds]);

  useEffect(() => {
    if (serverRelatedSigRef.current !== serverRelatedSig) {
      serverRelatedSigRef.current = serverRelatedSig;
      const ids = serverRelatedIds;
      const next = normalizeRelatedTemplateIds([...ids], templateCache);
      setSelectedTemplateIds((prev) => {
        if (
          prev.length === next.length &&
          prev.every((id, i) => id === next[i])
        ) {
          return prev;
        }
        return next;
      });
      return;
    }
    setSelectedTemplateIds((prev) => {
      const next = normalizeRelatedTemplateIds(prev, templateCache);
      if (
        prev.length === next.length &&
        prev.every((id, i) => id === next[i])
      ) {
        return prev;
      }
      return next;
    });
  }, [serverRelatedSig, templateCache, serverRelatedIds]);

  useEffect(() => {
    const uncachedIds = selectedTemplateIds.filter(
      (id) => !templateCache.has(id) && !loadingRef.current.has(id),
    );

    if (uncachedIds.length === 0) return;

    uncachedIds.forEach((id) => loadingRef.current.add(id));

    const load = async () => {
      try {
        const templates = await Promise.all(
          uncachedIds.map((id) =>
            getFetcher(`/spine-templates/${id}`).catch(() => ({ id })),
          ),
        );

        setTemplateCache((prev) => {
          const newCache = new Map(prev);
          templates.forEach((t) => t?.id && newCache.set(t.id, t));
          return newCache;
        });
      } finally {
        uncachedIds.forEach((id) => loadingRef.current.delete(id));
      }
    };

    load();
  }, [selectedTemplateIds, templateCache]);

  const handleAddTemplate = (template: Partial<SpineTemplate>) => {
    if (!template.id || selectedTemplateIds.includes(template.id)) return;
    setTemplateCache((prev) => new Map(prev).set(template.id, template));
    setSelectedTemplateIds((prev) => {
      const mergedCache = new Map(templateCache);
      mergedCache.set(template.id, template);
      return normalizeRelatedTemplateIds([...prev, template.id], mergedCache);
    });
  };

  const handleRemoveTemplate = (templateId: string) => {
    setSelectedTemplateIds((prev) => prev.filter((id) => id !== templateId));
  };

  const handleReorderTemplates = (nextIds: string[]) => {
    setSelectedTemplateIds(nextIds);
  };

  const handleSave = async () => {
    if (!saveEndpoint) return;
    if (isAvatarMode) {
      await saveRelatedTemplates({
        key: "relatedSpineTemplateIds",
        value: selectedTemplateIds,
      });
    } else {
      await saveRelatedTemplates({
        relatedTemplateIds: selectedTemplateIds,
      });
    }
  };

  const templateListExcludeId = isAvatarMode ? "" : spineTid || "";

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden">
      <div className="flex shrink-0 items-center justify-end">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isMutating || !saveEndpoint}
          className="shrink-0"
        >
          <Save className="mr-2 h-4 w-4" />
          {isMutating ? "保存中..." : "保存"}
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
        <TemplateList
          currentTid={templateListExcludeId}
          selectedTemplateIds={selectedTemplateIds}
          onSelectTemplate={handleAddTemplate}
          templateCache={templateCache}
        />

        <SelectedTemplatesList
          selectedTemplateIds={selectedTemplateIds}
          templateCache={templateCache}
          onRemoveTemplate={handleRemoveTemplate}
          onReorderTemplates={handleReorderTemplates}
        />
      </div>
    </div>
  );
}
